import type { MetaFunction } from "@remix-run/react"
import {
  h1Style,
  h1Wrap,
  headerTextStyle,
  articleCompareImages,
  articleImageStyle,
  figCaptionStyle,
} from "./article.css"
import Footer from "~/components/Footer"
import Link from "~/components/Link"
import { page } from "~/shared/styles.css"
import Logo from "~/components/Logo"
import StickyHeader from "~/components/StickyHeader"
import { ActionBtn, P, Section } from "~/components/articleComponents"

export type PreviewImage = {
  url: string
  size: { w: number; h: number }
}

export type Url = {
  url: string
  initial?: PreviewImage | null
  loading?: boolean
}

const TITLE = "Try Rescape AI before you buy"
const DESCRIPTION =
  "Rescape AI new offer: Generate one free image with Rescape AI and see why we're better than the competition"
const URL = "https://rescapeai.com/article/free-trial-ai-garden-design"
const IMAGE = "https://rescapeai.com/articles/firepit-space.webp"

export const meta: MetaFunction = () => {
  return [
    {
      title: TITLE,
    },
    {
      name: "description",
      content: DESCRIPTION,
    },
    {
      property: "og:title",
      content: TITLE,
    },
    {
      property: "og:description",
      content: DESCRIPTION,
    },
    {
      property: "og:image",
      content: IMAGE,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: URL,
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: TITLE,
        description: DESCRIPTION,
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2025-10-22",
        dateModified: "2025-10-22",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: IMAGE,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": URL,
        },
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/articles/free-trial-cover.webp)",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />

          <h1 className={headerTextStyle}>
            Rescape AI Introduces Free AI Garden Design Image for Every New User
          </h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />
      <div className={page}>
        <Section>
          <P>
            We’re offering every new user one free AI-generated garden design image to try
            the tool before purchasing credits. After trying, you’ll see why so many
            people come to Rescape AI for garden design over other AI competitors.
          </P>
        </Section>
        <ActionBtn>Start Free Trial</ActionBtn>
        <Section>
          <P>
            The update allows anyone to upload a photo of their outdoor space and receive
            a free AI <Link href="/article/ai-garden-design">garden design concept</Link>.
            Whether planning a complete garden transformation or exploring fresh ideas,
            users can now see the potential of their space with no upfront commitment.
          </P>
          <figure>
            <div className={articleCompareImages}>
              <img
                src={"/articles/free-trial-wildflowers.webp"}
                alt={"Wildflower meadow designed with the free trial on Rescape AI"}
                loading="lazy"
              />
              <img
                src={"/articles/free-trial-roses.webp"}
                alt={"Rose garden designed with the free trial on Rescape AI"}
                loading="lazy"
              />
            </div>
            <img
              className={articleImageStyle}
              src={"/articles/free-trial-contemporary.webp"}
              alt={"Contemporary garden designed with the free trial on Rescape AI"}
              loading="lazy"
              style={{
                height: "320px",
                width: "100%",
                objectFit: "cover",
                objectPosition: "0% 80%",
                marginTop: "2rem",
              }}
            />
            <figcaption className={figCaptionStyle}>
              Different <Link href="/garden-design-guide">AI generate styles</Link> of the
              same garden
            </figcaption>
          </figure>
        </Section>
        <Section>
          <P>
            We want people to see how their gardens could look so we’re giving every new
            user a free image to let them experience the{" "}
            <Link href="/article/ai-garden-design">AI garden design</Link> technology
            before deciding if it's right for them.
          </P>
          <P>
            Rescape AI helps homeowners generate realistic garden concepts from a single
            photo. Users can explore different landscaping styles, planting ideas, layouts
            and outdoor living concepts, helping visualise possibilities before investing
            time and money into a project.
          </P>
          <P>
            The new free image offer provides an accessible introduction to free AI garden
            design, allowing users to test the platform's capabilities and discover how
            quickly AI can generate outdoor design inspiration.
          </P>
        </Section>
        <Section>
          <img
            className={articleImageStyle}
            src="/articles/free-trial-cottage.webp"
            style={{ marginTop: "1rem", marginBottom: "1rem" }}
            alt="Visualise your garden with a cottage garden aesthetic for free"
          />
          <P>
            <Link href="/hello/signup">Join now to trial how Rescape AI works.</Link>
          </P>
          <P>
            The update is available immediately for all new accounts. After generating a
            complimentary image, you can continue exploring additional designs through
            Rescape AI's credit packs, unlocking more creative possibilities for outdoor
            spaces.
          </P>
        </Section>

        <Section>
          <ActionBtn>Start now</ActionBtn>
        </Section>
        <Footer showLinks={false} />
      </div>
    </>
  )
}

export default Index
